import { Link } from '@tanstack/react-router';
import type { components } from '@/api/generated/schema';
import { cn } from '@pixlide/ui';

type Subscription = components['schemas']['PixlideApplicationFeaturesBillingSubscriptionResponse'];

/**
 * Carte de consommation d'abonnement, en bas de la barre latérale (reprise de la maquette).
 *
 * Affiche la consommation d'ÉCRANS, qui est la ressource réellement contrainte par la
 * formule et la première cause de blocage rencontrée par un client qui grandit. Le lien de
 * montée en gamme est présenté dès que le quota est proche, pas seulement quand il est
 * atteint : découvrir la limite au moment d'installer un écran en salle est le pire moment.
 */
export function PlanUsageCard({ subscription }: { subscription: Subscription }) {
  const allowance = Math.max(subscription.screenAllowance, 1);
  const used = subscription.activeScreens;
  const ratio = Math.min(used / allowance, 1);
  const isNearLimit = ratio >= 0.75;

  return (
    <div className="rounded-card bg-white p-4 shadow-card">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
          Offre {subscription.planName}
        </span>

        <Link to="/offres" className="text-xs font-medium text-brand-700 hover:underline">
          Mettre à niveau
        </Link>
      </div>

      <div
        className="mt-3 h-1.5 overflow-hidden rounded-pill bg-ink-100"
        // Barre de progression accessible : la valeur est lisible par un lecteur d'écran,
        // pas seulement déduite d'une largeur en pixels.
        role="progressbar"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={allowance}
        aria-label="Écrans utilisés"
      >
        <div
          className={cn(
            'h-full rounded-pill transition-[width]',
            isNearLimit ? 'bg-warning-500' : 'bg-brand-500',
          )}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>

      <p className="mt-2 text-xs text-ink-500">
        {used}/{allowance} écran{allowance > 1 ? 's' : ''} utilisé{used > 1 ? 's' : ''}
      </p>
    </div>
  );
}
